import type { VersionedData } from '../shared/VersionedData';
import { UserChoice } from '../adaptive-guidance/enums';

export interface AdaptiveGuidancePreferencesContent {
  /** Preseance #1 (mise hors service). No Settings UI exists for this yet — see L7_SPORT_PHILOSOPHY-style closure doc for AGS Phase 1's documented scope boundary; defaults to enabled. */
  readonly agsEnabled: boolean;
  /** One user choice per logical date — the minimum needed to honor a refusal without re-asking. */
  readonly decisions: Readonly<Record<string, UserChoice>>;
}

export const DEFAULT_ADAPTIVE_GUIDANCE_PREFERENCES: AdaptiveGuidancePreferencesContent = {
  agsEnabled: true,
  decisions: {},
};

const CONTENT_VERSION = 1;

export function toAdaptiveGuidancePreferencesData(
  content: AdaptiveGuidancePreferencesContent,
): VersionedData {
  return { version: CONTENT_VERSION, payload: { ...content } };
}

function isChoiceMap(value: unknown): value is Readonly<Record<string, UserChoice>> {
  if (typeof value !== 'object' || value === null) return false;
  return Object.values(value as Record<string, unknown>).every(
    (entry) => entry === UserChoice.accepted || entry === UserChoice.rejected,
  );
}

/** Returns undefined for missing, malformed or future-versioned payloads rather than throwing. */
export function fromAdaptiveGuidancePreferencesData(
  data: VersionedData | undefined,
): AdaptiveGuidancePreferencesContent | undefined {
  if (!data || data.version !== CONTENT_VERSION) return undefined;
  const { agsEnabled, decisions } = data.payload;
  if (typeof agsEnabled !== 'boolean' || !isChoiceMap(decisions)) return undefined;
  return { agsEnabled, decisions };
}
